// Outer
import React, { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Grid, Typography, Box, Paper, Link, Button } from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas, faTrash } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { useSnackbar } from 'notistack';

// Inner
import HeaderBar from '@/common/layouts/HeaderBar';
import DefaultLayout from '@/common/layouts/DefaultLayout';
import Division from '@/common/components/Division';
import HeroBanner from '@/common/components/HeroBanner';
import SimpleDrawer from '@/common/components/SimpleDrawer';
import UpdateFAQForm from '@/common/pages/home/faq/UpdateFAQForm';
import CreateFAQForm from '@/common/pages/home/faq/CreateFAQForm';
import Footer from '@/common/layouts/Footer';
import { ApiError, AllFAQData } from '@/common/api/types';
import { getServerSidePropsPrefetch } from '@/common/utils/ssr';
import { ssrMenuItems } from '@/common/hooks/useMenuItems';
import { queries } from '@/common/api/operations';
import { useApiContext } from '@/common/contexts/ApiContext';
import { useUserContext } from '@/common/contexts/UserContext';
import { useSanitizeHtml } from '@/common/hooks/useSanitizeHtml';

library.add(fas, far);

export default function FAQPage() {
  const [isFAQDrawerUpdate, setFAQDrawerUpdate] = useState(false);
  const [isFAQDrawerCreate, setFAQDrawerCreate] = useState(false);
  const [faqId, setFaqId] = useState(0);

  const { apiClient } = useApiContext();
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin }: any = useUserContext();

  const faqsQuery = useQuery(
    queries.faqs.get,
    () => apiClient.faqs.get()
  );
  const allFAQs = faqsQuery.data;

  const handleDissmissFAQDrawerUpdate = () => {
    setFAQDrawerUpdate(false);
    setFaqId(0);
  };

  const handleDissmissFAQDrawerCreate = () => {
    setFAQDrawerCreate(false);
  };

  function onSuccess() {
    enqueueSnackbar('FAQ deleted', { variant: 'success' });
    faqsQuery.refetch();
  }
  function onError(error: ApiError | Error) {
    enqueueSnackbar(error.message, { variant: 'error' });
    Sentry.captureException(error);
  }

  const deleteFAQMutation = useMutation<
    any,
    ApiError | Error,
    any
  >(apiClient.faq.delete, {
    onSuccess,
    onError
  })

  const deleteFAQ = (id: number) => {
    deleteFAQMutation.mutate({ id });
  }

  return (
    <>
      <HeaderBar />
      <DefaultLayout>

        <SimpleDrawer
          anchor="right"
          size="large"
          isOpen={isFAQDrawerUpdate}
          strictClose={true}
          onClose={handleDissmissFAQDrawerUpdate}
        >
          <UpdateFAQForm
            faqId={faqId}
            refetch={faqsQuery.refetch}
            onClose={handleDissmissFAQDrawerUpdate}
            faqs={allFAQs}
          />
        </SimpleDrawer>

        <SimpleDrawer
          anchor="right"
          size="large"
          isOpen={isFAQDrawerCreate}
          strictClose={true}
          onClose={handleDissmissFAQDrawerCreate}
        >
          <CreateFAQForm
            faqId={faqId}
            refetch={faqsQuery.refetch}
            onClose={handleDissmissFAQDrawerCreate}
            faqs={allFAQs}
          />
        </SimpleDrawer>

        <Grid container direction="column">
          <HeroBanner />

          <Box display="flex" justifyContent="space-between" marginBottom={2}>
            <Typography variant="h5">Frequently Asked Questions</Typography>

            {
              isAdmin && (
                <Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setFAQDrawerCreate(true)}
                  >Add new Question</Button>
                </Box>
              )
            }
          </Box>

          <Paper>
            <Box
              display="flex"
              flexDirection="column"
              flexWrap="wrap"
              justifyContent="space-evenly"
              padding={3}
            >
              {
                allFAQs?.map((faq: AllFAQData) => (
                  <Box
                    marginBottom={4}
                    key={faq.id}
                    maxWidth={800}
                    style={{ position: "relative" }}
                  >
                    {
                      isAdmin && (
                        <Link
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            right: -18,
                            top: 5
                          }}
                          onClick={() => deleteFAQ(faq.id)}
                        >
                          <FontAwesomeIcon
                            size="1x"
                            icon={faTrash}
                          />
                        </Link>
                      )
                    }

                    <Typography variant="h6" gutterBottom={true} >{faq.question}</Typography>
                    {
                      isAdmin ? (
                        <Link
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setFaqId(faq.id);
                            setFAQDrawerUpdate(true);
                          }}
                        >
                          <div
                            className='clear-indents'
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            dangerouslySetInnerHTML={{ __html: useSanitizeHtml(faq.answer) }}
                          />
                        </Link>
                      ) : (
                        <div
                          className='clear-indents'
                          // eslint-disable-next-line react-hooks/rules-of-hooks
                          dangerouslySetInnerHTML={{ __html: useSanitizeHtml(faq.answer) }}
                        />
                      )
                    }

                  </Box>
                ))
              }

            </Box>
          </Paper>

          <Division />
        </Grid>
      </DefaultLayout>
      <Footer />
    </>
  );
}

export const getServerSideProps = getServerSidePropsPrefetch([
  ssrMenuItems,
]);
